import { type RefObject, useCallback, useEffect, useState } from 'react';

/**
 * Puts one element into fullscreen and reports whether it currently is.
 *
 * Deliberately local to the viewer rather than extracted from hooks/useChartActions.ts:
 * that hook has sixteen consumers and is coupled to Chart.js (it calls chartRef.resize()),
 * and it tracks fullscreen by element identity while this needs a plain boolean. Sharing
 * one implementation is worth doing, but not as a side effect of this refactor.
 *
 * What this fixes: the previous version assigned `document.onfullscreenchange` from inside
 * the click handler. That is a single-slot property, so it clobbered any other handler on
 * the page, and - being assigned only on click - it did not notice a fullscreen exit
 * (Escape, say) until the button had been pressed at least once.
 */
interface ViewerFullscreen {
    isFullScreen: boolean;
    toggleFullScreen: () => Promise<void>;
}

export function useViewerFullscreen(targetRef: RefObject<HTMLElement | null>): ViewerFullscreen {
    const [isFullScreen, setIsFullScreen] = useState(false);

    useEffect(() => {
        const onChange = () => setIsFullScreen(Boolean(document.fullscreenElement));

        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const toggleFullScreen = useCallback(async () => {
        const target = targetRef.current;

        if (!target || !document.fullscreenEnabled) {
            return;
        }

        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            } else {
                await target.requestFullscreen({ navigationUI: 'hide' });
            }
        } catch {
            // A browser may refuse (no user gesture, an iframe without permission); the viewer
            // then stays as it is, and `fullscreenchange` never fires to say otherwise.
        }
    }, [targetRef]);

    return { isFullScreen, toggleFullScreen };
}
